/* eslint-disable react-hooks/exhaustive-deps */

import { version as portalReactVersion, useEffect, useState } from 'react';
import { useDomainTheme } from '../components';
import { Nullable } from '../types';
import { isMajorVersionMatch } from '../util';

interface DynamicModuleResponse<T> {
  module: T | undefined;
  loaded: boolean;
  error: Nullable<string>;
}

interface WithReactVersion { reactVersion?: string }

const dynamicModuleCache: Record<string, unknown> = {};

export const useDynamicModule = <T extends WithReactVersion>(name: string, defaults: T): DynamicModuleResponse<T> => {
  const { domainTheme } = useDomainTheme();
  const build = import.meta.env.VITE_BUILD || import.meta.env.REACT_APP_BUILD || '';
  const cacheKey = `${name}|${domainTheme}|${build}`;
  const cached = dynamicModuleCache[cacheKey] as T | undefined;
  const [module, setModule] = useState<T | undefined>(() => cached ?? defaults);
  const [loaded, setLoaded] = useState<boolean>(!!cached);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    // Check cache first
    if (cached) return;

    const moduleURL = `${new URL(import.meta.url).origin}/js/module/${name}?domain=${domainTheme}${build ? `&build=${build}` : ''}`;

    import(/* @vite-ignore */ moduleURL)
      .then((moduleResult: T) => {
        if (!isMounted) return;

        let finalModule: T;
        let finalError: string | null = null;

        if (typeof moduleResult === 'object') {
          if (moduleResult.reactVersion && isMajorVersionMatch(moduleResult.reactVersion, portalReactVersion)) {
            finalModule = { ...defaults, ...moduleResult };
          } else {
            finalModule = { ...defaults };
            finalError = `Imported module React version ${moduleResult.reactVersion || ''} is incompatible with Portal version ${portalReactVersion}. Importing default modules.`;
          }
        } else {
          finalModule = { ...defaults };
          finalError = 'Imported module is not an object';
        }

        dynamicModuleCache[cacheKey] = finalModule;
        if (isMounted) {
          setModule(finalModule);
          setError(finalError);
          setLoaded(true);
        }
      })
      .catch((errorResult: string) => {
        if (!isMounted) return;
        const fallbackModule = { ...defaults };
        dynamicModuleCache[cacheKey] = fallbackModule;
        setModule(fallbackModule);
        setError(errorResult);
        setLoaded(true);
      });

    return () => {
      isMounted = false;
    };
  }, [defaults, domainTheme, cacheKey]);

  return { module, error, loaded };
};
